/**
 * Start-up choreography of the try-on (A110W).
 *
 * The first seconds after the camera appears carry one-off costs that the
 * steady state never pays again: the first inferences of the tracker (wasm
 * tier-up, XNNPACK weight packing, GPU-delegate shaders — the first one can
 * take tens of times longer than the following ones), shader programs and
 * texture uploads. Shown as they come, they read as a stuttering frame on a
 * jumpy video; fed to the quality governor, they read as a slow device and
 * cost a needless step down (and the step back up ten seconds later).
 *
 * This class decides, from timestamps and inference durations only:
 *  - when the tracker is warm (its recent inference times are steady);
 *  - when the glasses may appear (programs ready, warm tracker, face tracked),
 *    and their fade-in from that moment;
 *  - when the frame lock may engage (after the reveal: before it, the live
 *    camera image is shown, never one paced by cold inferences);
 *  - from when the governor may judge the device (the first frames excluded).
 *
 * Nothing here can block the try-on: each wait has a time limit.
 */
export interface StartupPhaseParams {
  /** Face results needed before the tracker can count as warm (the first ones carry one-off costs). */
  minResults: number;
  /** The last `steadyResults` face inferences must each stay under the cold threshold… */
  steadyResults: number;
  /** …which is `coldRatio` × the median of the recent ones, but never under `coldFloorMs`. */
  coldRatio: number;
  coldFloorMs: number;
  /** Stop waiting for a steady tracker this long after the render loop started (slow, noisy or faceless starts). */
  maxWarmupMs: number;
  /** Stop waiting for the programs this long after the render loop started. */
  maxProgramsMs: number;
  /** Fade-in of the glasses once revealed (ms). */
  revealFadeMs: number;
  /**
   * The governor starts judging this long after the render loop started and
   * the GPU warm-up ended: those first frames carry one-off costs.
   */
  governorSettleMs: number;
}

export const DEFAULT_STARTUP_PARAMS: StartupPhaseParams = {
  minResults: 3,
  steadyResults: 2,
  coldRatio: 2,
  coldFloorMs: 90,
  maxWarmupMs: 2500,
  maxProgramsMs: 4000,
  revealFadeMs: 220,
  governorSettleMs: 600,
};

export class StartupPhase {
  readonly params: StartupPhaseParams;
  private faceInfers: number[] = [];
  private results = 0;
  private warm = false;
  private programs = false;
  private programsAt: number | null = null;
  private loopAt: number | null = null;
  private revealAt: number | null = null;
  private warmAt: number | null = null;

  constructor(params: Partial<StartupPhaseParams> = {}) {
    this.params = { ...DEFAULT_STARTUP_PARAMS, ...params };
  }

  /** The render loop drew its first frame (camera image on screen). */
  loopStarted(nowMs: number): void {
    if (this.loopAt === null) this.loopAt = nowMs;
  }

  /** Shader programs and textures are on the GPU (or the attempt is over). */
  programsReady(nowMs: number): void {
    if (this.programs) return;
    this.programs = true;
    this.programsAt = nowMs;
  }

  get trackerWarm(): boolean {
    return this.warm;
  }

  get revealed(): boolean {
    return this.revealAt !== null;
  }

  get revealedAt(): number | null {
    return this.revealAt;
  }

  get trackerWarmAt(): number | null {
    return this.warmAt;
  }

  /**
   * One tracker result. Returns true when this result makes the tracker warm
   * (the caller then starts its inference statistics afresh).
   */
  inference(inferMs: number, face: boolean, nowMs: number): boolean {
    this.results++;
    if (this.warm || !face || !Number.isFinite(inferMs)) return false;
    this.faceInfers.push(inferMs);
    if (this.faceInfers.length > Math.max(5, this.params.minResults)) this.faceInfers.shift();
    if (this.faceInfers.length < Math.max(this.params.minResults, this.params.steadyResults)) return false;
    const recent = this.faceInfers.slice(-this.params.steadyResults);
    const threshold = Math.max(this.params.coldFloorMs, this.params.coldRatio * median(this.faceInfers));
    if (recent.every((ms) => ms <= threshold)) return this.becomeWarm(nowMs);
    return false;
  }

  /**
   * Once per rendered frame: applies the time limits and reveals the glasses
   * when everything is ready. `tracking`: the state machine tracks a face.
   */
  update(nowMs: number, tracking: boolean): void {
    if (this.loopAt === null) return;
    const elapsed = nowMs - this.loopAt;
    if (!this.warm && elapsed >= this.params.maxWarmupMs && this.results > 0) this.becomeWarm(nowMs);
    if (!this.programs && elapsed >= this.params.maxProgramsMs) this.programsReady(nowMs);
    if (this.revealAt === null && tracking && this.warm && this.programs) this.revealAt = nowMs;
  }

  /** Frame opacity actually drawn: the state machine's, held at 0 until the reveal, then faded in. */
  gate(stateOpacity: number, nowMs: number): number {
    if (this.revealAt === null) return 0;
    const fade = this.params.revealFadeMs > 0 ? (nowMs - this.revealAt) / this.params.revealFadeMs : 1;
    return Math.min(stateOpacity, fade < 0 ? 0 : fade > 1 ? 1 : fade);
  }

  /** The glasses are being prepared (camera on screen, engine not steady yet): worth a word to the user. */
  get preparing(): boolean {
    return this.loopAt !== null && this.revealAt === null && (!this.warm || !this.programs);
  }

  /** Frame lock (display paced by inference) only once the tracker keeps a steady pace and the glasses are shown. */
  get lockAllowed(): boolean {
    return this.warm && this.revealAt !== null;
  }

  /** The governor may judge the device (its measurements must start afresh at that moment). */
  governorReady(nowMs: number): boolean {
    if (this.loopAt === null || this.programsAt === null) return false;
    return nowMs - Math.max(this.loopAt, this.programsAt) >= this.params.governorSettleMs;
  }

  private becomeWarm(nowMs: number): boolean {
    this.warm = true;
    this.warmAt = nowMs;
    return true;
  }
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}
